import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Container } from '@mui/system'
import { Divider, Typography } from '@mui/material'

//components
import LibraryGame from './components/LibraryGame'
import ArchiveDialog from './components/ArchiveDialog'
import ClaimBanner from 'pages/GamesList/components/redesign/ClaimBanner'
import EarnGiftCards from 'components/EarnGiftCards'
import GenericUnlockGmailRewardsTask from 'components/Task/GenericUnlockGmailRewardsTask'
//contexts
import AuthContext from 'context/AuthContext'
//constants
import { CLAIM_ALL_SOURCE } from 'constants/index'
//services
import { archiveActivatedOffer } from 'services/offersService'
//utils
import { handleError } from 'utils/utils'

/** How long the "Archived" banner stays up. */
const ARCHIVED_BANNER_MS = 4000

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const Section = ({ title, aside, games, onArchive }: any) =>
  games.length > 0 ? (
    <section className="gameLibrarySection" aria-label={title}>
      <header className="gameLibrarySectionHeader">
        <h2 className="gamesRedesignSectionTitle">{title}</h2>
        <span className="gameLibrarySectionCount">{aside}</span>
      </header>
      <ul className="gameLibraryList">
        {games.map((game: any) => (
          <LibraryGame key={game.unifiedAppId} game={game} onArchive={onArchive} />
        ))}
      </ul>
    </section>
  ) : null

const Content = ({ gameLibrary }: any) => {
  const { user } = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<any>(null)
  const [isConfirmOpen, setConfirmOpen] = useState(false)
  const [archived, setArchived] = useState<any>(null)

  useEffect(() => {
    if (!archived) {
      return
    }

    const timer = setTimeout(() => setArchived(null), ARCHIVED_BANNER_MS)

    return () => clearTimeout(timer)
  }, [archived])

  // The backend sends the games with a valid offer in progress first, each with its offer. An
  // archived offer keeps its progress but waits in the history until the user plays it again.
  const games = gameLibrary?.filter((game: any) => game.gameDetails) ?? []
  const isInProgress = (game: any) => game.activatedOffer && !game.activatedOffer.isArchived
  const inProgress = games.filter(isInProgress)
  const history = games.filter((game: any) => !isInProgress(game))

  const confirmArchive = (game: any) => {
    setConfirming(game)
    setConfirmOpen(true)
  }

  const archive = async () => {
    setConfirmOpen(false)
    try {
      await archiveActivatedOffer(confirming.activatedOffer.id)
      queryClient.setQueryData('gameLibrary', (library: any) =>
        library?.map((game: any) =>
          game.unifiedAppId === confirming.unifiedAppId
            ? { ...game, activatedOffer: { ...game.activatedOffer, isArchived: true } }
            : game
        )
      )
      setArchived(confirming)
    } catch (err) {
      handleError(err)
    }
  }

  return (
    <>
      <div className="gamesRedesignTopBanners">
        <ClaimBanner source={CLAIM_ALL_SOURCE.LIBRARY} />
        {archived && (
          <div className="gamesRedesignTopBanner gameLibraryArchivedBanner" role="status">
            <img className="gameLibraryArchivedIcon" src={archived.gameDetails.icon_url} alt="" />
            <span className="gameLibraryArchivedText">
              <span>{archived.gameDetails.name} Archived</span>
              <span className="gameLibraryArchivedNote">Progress Kept - Play it again anytime</span>
            </span>
          </div>
        )}
      </div>
      <Container maxWidth="sm">
        {!games.length && (
          <div className="gameLibraryDescription">
            <Typography variant="inherit">No rewards earned yet...</Typography>
            <EarnGiftCards />
            {!user?.hasParsingEnabled && (
              <span>
                <br />
                <Divider variant="fullWidth" />
                <br />
                <GenericUnlockGmailRewardsTask />
              </span>
            )}
          </div>
        )}
        <Section
          title="Continue"
          aside={plural(inProgress.length, 'offer')}
          games={inProgress}
          onArchive={confirmArchive}
        />
        <Section
          title="Offer History"
          aside={plural(history.length, 'completed offer')}
          games={history}
        />
      </Container>
      {confirming && (
        <ArchiveDialog
          game={confirming}
          open={isConfirmOpen}
          onKeep={() => setConfirmOpen(false)}
          onArchive={archive}
          onExited={() => setConfirming(null)}
        />
      )}
    </>
  )
}

export default Content
